import { MissionBadge } from '@/features/missions/components/MissionBadge'
import { RecordingStateIdle } from '@/features/nectar/components/audio-recording/RecordingStateIdle'
import { RecordingStateRecorded } from '@/features/nectar/components/audio-recording/RecordingStateRecorded'
import { RecordingStateRecording } from '@/features/nectar/components/audio-recording/RecordingStateRecording'
import { Text } from '@/ui-kit'
import { toast } from 'sonner'
import { useSubmitNectarTask } from '../../api'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import type { TaskRendererProps } from '../task-types'

export const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

export function AudioRecordingTask({ task, projectId, onSubmitted }: TaskRendererProps) {
  const {
    state,
    seconds,
    audioUrl,
    blob,
    startRecording,
    stopRecording,
    reset,
    markSubmitting,
    markSubmitted,
    markRecorded
  } = useAudioRecorder()
  const { mutateAsync: submitTask } = useSubmitNectarTask()

  const phrase = typeof task.params?.phrase === 'string' ? task.params.phrase : ''
  const emotion =
    typeof task.params?.emotion === 'string'
      ? task.params.emotion
      : typeof task.metadata?.emotion === 'string'
        ? task.metadata.emotion
        : null

  const submit = async () => {
    if (!blob) return
    markSubmitting()
    try {
      const ext = blob.type.includes('webm') ? 'webm' : 'ogg'
      const file = new File([blob], `recording.${ext}`, { type: blob.type })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('duration', String(seconds))

      await submitTask({ projectId, taskId: task.id, data: formData })
      toast.success('Submitted!')
      markSubmitted()
      onSubmitted(task.id)
    } catch {
      toast.error('Failed to submit recording')
      markRecorded()
    }
  }

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        {task.instruction && (
          <Text
            type="body2-regular"
            className="text-gray-90 m-auto block rounded-xl border border-solid border-gray-100 p-2"
          >
            {task.instruction}
          </Text>
        )}
        {phrase && <span className="text-h5 block text-center leading-[120%]">&ldquo;{phrase}&rdquo;</span>}
        {emotion && (
          <div className="mt-4 flex">
            <MissionBadge variant="warning">{emotion}</MissionBadge>
          </div>
        )}
        <div className="flex w-full flex-col items-center gap-5">
          {state === 'idle' && <RecordingStateIdle onStartRecording={startRecording} />}
          {state === 'recording' && <RecordingStateRecording seconds={0} onStopRecording={stopRecording} />}
          {(state === 'recorded' || state === 'submitting' || state === 'submitted') && (
            <RecordingStateRecorded
              audioUrl={audioUrl}
              seconds={seconds}
              isSubmitting={state === 'submitting'}
              onReRecord={() => {
                reset()
                startRecording()
              }}
              onSubmit={submit}
            />
          )}
        </div>
      </div>
    </div>
  )
}
